import { memoize } from 'lodash';
import { getTenantNamespace } from './tenant';

/**
 * Gets a boot flag value which is basically a feature flag value, but is available when the application starts (i.e.
 * before the user has authenticated). Function is memoized so it only tries to resolve a given flag once.
 */
export const getBootFlag = memoize(_getBootFlag);

function _getBootFlag<T extends string | number | boolean>(flag: BootFlag<T>): T {
  try {
    // We're using `parent` here instead of `window` so that if/when this is used inside the Grafana iframe, it'll use
    // the values loaded by Cloud UI (when used in Cloud, `parent` should just resolve to the regular `window`)
    const windowValues = parent.chronoBootFlags;

    if (!isJsObject(windowValues)) {
      throw new Error(`Expected a JavaScript object for BootFlags but received '${windowValues}'`);
    }

    if (!(flag.key in windowValues)) {
      throw new Error(`Key '${flag.key}' is missing from BootFlag values`);
    }

    const valueFromWindow = windowValues[flag.key];
    if (!flag.isValid(valueFromWindow)) {
      throw new Error(`Invalid value '${valueFromWindow}' for flag '${flag.key}'`);
    }

    return valueFromWindow;
  } catch (err) {
    const fallbackValue = typeof flag.defaultValue === 'function' ? flag.defaultValue() : flag.defaultValue;
    console.error(`Using default value '${fallbackValue}' for BootFlag '${flag.key}'`, err);
    return fallbackValue;
  }
}

// The default for Sentry is disabled in RC environments (wrapped in a function to avoid evaluating this until needed)
function sentryDefault() {
  const tenantNamespace = getTenantNamespace();
  // Temporary. Will remove after testing.
  if (tenantNamespace === 'rc-eunice-wong') {
    return 'enabled';
  }

  return tenantNamespace.startsWith('rc-') || tenantNamespace === 'localhost' ? 'disabled' : 'enabled';
}

/**
 * A subset of feature flag values available when the application starts.
 */
export const BootFlags = {
  /**
   * Controlled by the `fullstory-ui-mode` flag in LD.
   */
  FULL_STORY_MODE: stringUnionFlag('fullStoryMode', 'disabled', ['enabled', 'disabled', 'relayed']),

  /**
   * Controlled by the `sentry-ui-mode` flag in LD.
   */
  SENTRY_MODE: stringUnionFlag('sentryMode', sentryDefault, ['enabled', 'disabled']),

  /**
   * Controlled by the `doorbell-ui-mode` flag in LD.
   */
  DOORBELL_MODE: stringUnionFlag('doorBellMode', 'disabled', ['internal', 'external', 'disabled']),

  /**
   * Controlled by the `ui-network-monitoring-mode` flag in LD.
   */
  NETWORK_MONITORING_ENABLED: stringUnionFlag('networkMonitoringEnabled', 'false', ['true', 'false']),

  /**
   * Controlled by the `chameleon-ui-mode` flag in LD. Experimental potential replacement for
   * user guiding.
   */
  CHAMELEON_MODE: stringUnionFlag('chameleonMode', 'disabled', ['enabled', 'disabled']),

  /**
   * Controlled by the `panw-rebrand-stub-year` flag in LD. Enables PANW rebranding.
   */
  PANW_REBRAND_STUB_YEAR: booleanFlag('panwRebrandStubYear', false),

  /**
   * Controlled by the `enable-new-logo-stub-year` flag in LD. Enables new XCOR logo & name.
   */
  ENABLE_NEW_LOGO_STUB_YEAR: booleanFlag('enableNewLogoStubYear', false),
};

export interface BootFlag<T extends string | number | boolean> {
  /**
   * The key on window.chronoBootFlags that contains the boot flag's value.
   */
  key: string;

  /**
   * Default value to use if something goes wrong.
   */
  defaultValue: T | DefaultValueFactory<T>;

  /**
   * Type guard to make sure the `unknown` value from the window is a valid value for this flag.
   */
  isValid(windowValue: unknown): windowValue is T;
}

type DefaultValueFactory<T> = () => T;

// Helper to define a boot flag that has a string union value
export function stringUnionFlag<const T extends readonly string[]>(
  key: string,
  defaultValue: T[number] | DefaultValueFactory<T[number]>,
  validValues: T
): BootFlag<T[number]> {
  return {
    key,
    defaultValue,
    isValid: (windowValue: unknown): windowValue is T[number] => {
      // Loop through the valid values and if the windowValue is found, it's valid
      for (const valid of validValues) {
        if (windowValue === valid) return true;
      }
      return false;
    },
  };
}

// Helper to define a boot flag that has a boolean value
export function booleanFlag(key: string, defaultValue: boolean | DefaultValueFactory<boolean>): BootFlag<boolean> {
  return {
    key,
    defaultValue,
    isValid: (windowValue): windowValue is boolean => typeof windowValue === 'boolean',
  };
}

// Helper to define a boot flag that has a numeric value
export function numberFlag(key: string, defaultValue: number | DefaultValueFactory<number>): BootFlag<number> {
  return {
    key,
    defaultValue,
    isValid: (windowValue): windowValue is number => typeof windowValue === 'number',
  };
}

// Helper to define a boot flag that has a freeform string value (try to use StringUnion instead if possible)
export function stringFlag(key: string, defaultValue: string | DefaultValueFactory<string>): BootFlag<string> {
  return {
    key,
    defaultValue,
    isValid: (windowValue): windowValue is string => typeof windowValue === 'string',
  };
}

// Helper to check for a JavaScript object { ... } with string props
function isJsObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && Array.isArray(val) === false;
}

declare global {
  interface Window {
    // There should be a <script> tag in our HTML that loads these and attaches them to the global window object,
    // defined as `unknown` so we have to guard against possibly malformed data from the backend
    chronoBootFlags: unknown;
  }
}

function setBootFlag<T extends string | number | boolean>(flag: BootFlag<T>, value: T) {
  const flags = (window.chronoBootFlags ?? {}) as Record<string, unknown>;
  flags[flag.key] = value;
  window.chronoBootFlags = flags;
}

/**
 * Sets all available boot flags to their default values on the global window object. Meant for use in tests.
 */
export function setBootFlagsToDefaults() {
  if (process.env.NODE_ENV !== 'production') {
    let flagName: keyof typeof BootFlags;
    for (flagName in BootFlags) {
      const flag = BootFlags[flagName];
      const value = typeof flag.defaultValue === 'function' ? flag.defaultValue() : flag.defaultValue;
      setBootFlag(flag, value);
    }
  }
}
